import React, { useState, useRef } from "react";
import { FormField } from "../../types/formTypes";
import Odontogram from "../../../../components/odontogram/Odontogram";
import { useFileUpload } from "../../../../hooks/useFileUpload";
import { AppointmentData } from "@/dentconnect/src/pages/job-request/types";
import { v4 as uuidv4 } from "uuid";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";

interface FileUploadFieldProps {
	value: File[] | null;
	onChange: (files: File[]) => void;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
	value,
	onChange,
}) => {
	const [selectedFiles, setSelectedFiles] = useState<File[]>(value || []);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileUpload = (files: File[]) => {
		setSelectedFiles(files);
		onChange(files);
	};

	const { isDragging, dragHandlers } = useFileUpload({
		onFileUpload: handleFileUpload,
		enableFolderUpload: false,
	});

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files && files.length > 0) {
			const fileArray = Array.from(files);
			handleFileUpload(fileArray);
		}
	};

	const handleClick = () => {
		fileInputRef.current?.click();
	};

	return (
		<div className="w-full">
			<div
				{...dragHandlers}
				onClick={handleClick}
				className={`
          w-full border-2 border-dashed rounded-md px-6 py-8
          flex flex-col items-center justify-center
          cursor-pointer transition-colors
          ${
				isDragging
					? "border-blue-500 bg-blue-50"
					: "border-gray-300 hover:border-gray-400 bg-gray-50"
			}
        `}
			>
				<svg
					className="w-12 h-12 text-gray-400 mb-3"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
					/>
				</svg>
				<p className="text-sm text-gray-600 mb-1">
					{isDragging ? "Dateien hier ablegen" : "Dateien hochladen"}
				</p>
				<p className="text-xs text-gray-500">
					Klicken oder Dateien hierher ziehen
				</p>
			</div>

			<input
				ref={fileInputRef}
				type="file"
				multiple
				onChange={handleFileInputChange}
				className="hidden"
			/>

			{selectedFiles.length > 0 && (
				<div className="mt-3">
					<p className="text-sm text-gray-700 font-medium mb-1">
						Ausgewählte Dateien:
					</p>
					<ul className="text-sm text-gray-600 space-y-1">
						{selectedFiles.map((file, index) => (
							<li key={index} className="flex items-center gap-2">
								<span className="text-gray-400">•</span>
								{file.name}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

interface AppointmentFieldProps {
	field: FormField;
	value: AppointmentData[] | null;
	onChange: (appointments: AppointmentData[]) => void;
}

const AppointmentField: React.FC<AppointmentFieldProps> = ({
	field,
	value,
	onChange,
}) => {
	const appointments = value || [];
	const maxReached = field.maxAppointments
		? appointments.length >= field.maxAppointments
		: false;

	const addAppointment = () => {
		if (maxReached) return;

		const newAppointment: AppointmentData = {
			appointmentId: uuidv4(),
			values: {},
		};

		if (field.appointmentFields) {
			field.appointmentFields.forEach((subField) => {
				newAppointment.values[subField.id] = "";
			});
		}

		onChange([...appointments, newAppointment]);
	};

	const removeAppointment = (appointmentId: string) => {
		onChange(
			appointments.filter((apt) => apt.appointmentId !== appointmentId)
		);
	};

	const updateAppointmentField = (
		appointmentId: string,
		fieldId: string,
		fieldValue: string
	) => {
		onChange(
			appointments.map((apt) =>
				apt.appointmentId === appointmentId
					? {
							...apt,
							values: {
								...apt.values,
								[fieldId]: fieldValue,
							},
						}
					: apt
			)
		);
	};

	return (
		<div className="space-y-3">
			{appointments.map((appointment, index) => (
				<div
					key={appointment.appointmentId}
					className="border rounded-md p-4 bg-gray-50 space-y-3"
				>
					<div className="flex items-center justify-between mb-2">
						<span className="text-sm font-medium text-gray-700">
							Termin {index + 1}
						</span>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() =>
								removeAppointment(appointment.appointmentId)
							}
							className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
							aria-label={`Termin ${index + 1} entfernen`}
							data-testid={`remove-appointment-${index}`}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>

					{field.appointmentFields?.map((subField) => (
						<div key={subField.id} className="space-y-1">
							<label className="text-sm text-gray-700">
								{subField.label}
								{subField.required && (
									<span className="text-red-500 ml-1">*</span>
								)}
							</label>

							{subField.type === "text" && (
								<input
									type="text"
									value={
										appointment.values[subField.id] || ""
									}
									onChange={(e) =>
										updateAppointmentField(
											appointment.appointmentId,
											subField.id,
											e.target.value
										)
									}
									placeholder={subField.placeholder}
									className="w-full border rounded-md px-3 py-2"
									aria-label={subField.label}
									data-testid={`appointment-${index}-field-${subField.id}`}
								/>
							)}

							{subField.type === "textarea" && (
								<textarea
									value={
										appointment.values[subField.id] || ""
									}
									onChange={(e) =>
										updateAppointmentField(
											appointment.appointmentId,
											subField.id,
											e.target.value
										)
									}
									placeholder={subField.placeholder}
									rows={3}
									className="w-full border rounded-md px-3 py-2"
									aria-label={subField.label}
									data-testid={`appointment-${index}-field-${subField.id}`}
								/>
							)}

							{subField.type === "date" && (
								<input
									type="date"
									value={
										appointment.values[subField.id] || ""
									}
									onChange={(e) =>
										updateAppointmentField(
											appointment.appointmentId,
											subField.id,
											e.target.value
										)
									}
									className="w-full border rounded-md px-3 py-2"
									aria-label={subField.label}
									data-testid={`appointment-${index}-field-${subField.id}`}
								/>
							)}

							{subField.type === "time" && (
								<input
									type="time"
									value={
										appointment.values[subField.id] || ""
									}
									onChange={(e) =>
										updateAppointmentField(
											appointment.appointmentId,
											subField.id,
											e.target.value
										)
									}
									className="w-full border rounded-md px-3 py-2"
									aria-label={subField.label}
									data-testid={`appointment-${index}-field-${subField.id}`}
								/>
							)}

							{subField.type === "select" && subField.options && (
								<select
									value={
										appointment.values[subField.id] || ""
									}
									onChange={(e) =>
										updateAppointmentField(
											appointment.appointmentId,
											subField.id,
											e.target.value
										)
									}
									className="w-full border rounded-md px-3 py-2"
									aria-label={subField.label}
									data-testid={`appointment-${index}-field-${subField.id}`}
								>
									<option value="">Bitte wählen...</option>
									{subField.options.map(
										(option, optIndex) => (
											<option
												key={optIndex}
												value={option}
											>
												{option}
											</option>
										)
									)}
								</select>
							)}

							{subField.type === "radio" && subField.options && (
								<div className="space-y-2">
									{subField.options.map(
										(option, optIndex) => (
											<div
												key={optIndex}
												className="flex items-center gap-2"
											>
												<input
													type="radio"
													id={`${appointment.appointmentId}-${subField.id}-${optIndex}`}
													name={`${appointment.appointmentId}-${subField.id}`}
													value={option}
													checked={
														appointment.values[
															subField.id
														] === option
													}
													onChange={(e) =>
														updateAppointmentField(
															appointment.appointmentId,
															subField.id,
															e.target.value
														)
													}
													className="w-4 h-4"
													aria-label={`${subField.label} - ${option}`}
													data-testid={`appointment-${index}-field-${subField.id}-option-${optIndex}`}
												/>
												<label
													htmlFor={`${appointment.appointmentId}-${subField.id}-${optIndex}`}
													className="text-sm cursor-pointer"
												>
													{option}
												</label>
											</div>
										)
									)}
								</div>
							)}

							{subField.type === "checkbox" &&
								subField.options && (
									<div className="space-y-2">
										{subField.options.map(
											(option, optIndex) => {
												const currentValue =
													appointment.values[
														subField.id
													] || "";
												const selectedOptions =
													currentValue
														? currentValue.split(
																","
															)
														: [];
												const isChecked =
													selectedOptions.includes(
														option
													);

												return (
													<div
														key={optIndex}
														className="flex items-center gap-2"
													>
														<input
															type="checkbox"
															id={`${appointment.appointmentId}-${subField.id}-${optIndex}`}
															value={option}
															checked={isChecked}
															onChange={(e) => {
																let newSelectedOptions =
																	[
																		...selectedOptions,
																	];
																if (
																	e.target
																		.checked
																) {
																	newSelectedOptions.push(
																		option
																	);
																} else {
																	newSelectedOptions =
																		newSelectedOptions.filter(
																			(
																				opt
																			) =>
																				opt !==
																				option
																		);
																}
																updateAppointmentField(
																	appointment.appointmentId,
																	subField.id,
																	newSelectedOptions.join(
																		","
																	)
																);
															}}
															className="w-4 h-4"
															aria-label={`${subField.label} - ${option}`}
															data-testid={`appointment-${index}-field-${subField.id}-option-${optIndex}`}
														/>
														<label
															htmlFor={`${appointment.appointmentId}-${subField.id}-${optIndex}`}
															className="text-sm cursor-pointer"
														>
															{option}
														</label>
													</div>
												);
											}
										)}
									</div>
								)}

							{subField.type === "file" && (
								<input
									type="file"
									onChange={(e) => {
										const file = e.target.files?.[0];
										updateAppointmentField(
											appointment.appointmentId,
											subField.id,
											file ? file.name : ""
										);
									}}
									className="w-full border rounded-md px-3 py-2"
									aria-label={subField.label}
									data-testid={`appointment-${index}-field-${subField.id}`}
								/>
							)}
						</div>
					))}
				</div>
			))}

			<Button
				type="button"
				variant="outline"
				onClick={addAppointment}
				disabled={maxReached}
				className="w-full gap-2"
				aria-label="Termin hinzufügen"
				data-testid="add-appointment-button"
			>
				<Plus className="h-4 w-4" />
				{maxReached
					? `Maximum erreicht`
					: field.maxAppointments
						? `Bis zu ${
								field.maxAppointments - appointments.length
							} ${
								field.maxAppointments - appointments.length ===
								1
									? "Termin"
									: "Termine"
							} hinzufügen`
						: "Termin hinzufügen"}
			</Button>
		</div>
	);
};

interface FormPreviewFieldProps {
	field: FormField;
	value: any;
	onChange: (value: any) => void;
}

const FormPreviewField: React.FC<FormPreviewFieldProps> = ({
	field,
	value,
	onChange,
}) => {
	switch (field.type) {
		case "title":
			return (
				<h3 className="text-lg font-semibold mt-6 mb-2">
					{field.label}
				</h3>
			);

		case "description":
			return <p className="text-gray-600 text-sm mb-4">{field.label}</p>;

		case "text":
			return (
				<input
					type="text"
					value={value || ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={field.placeholder}
					className="w-full border rounded-md px-3 py-2"
				/>
			);

		case "textarea":
			return (
				<textarea
					value={value || ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={field.placeholder}
					rows={3}
					className="w-full border rounded-md px-3 py-2"
				/>
			);

		case "select":
			return (
				<select
					value={value || ""}
					onChange={(e) => onChange(e.target.value)}
					className="w-full border rounded-md px-3 py-2"
				>
					<option value="" disabled>
						Wähle eine Option
					</option>
					{field.options?.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			);

		case "radio": {
			const valueStr = typeof value === "string" ? value : "";
			const optionSet = new Set(field.options ?? []);
			const isOther =
				!!field.allowOther &&
				valueStr !== "" &&
				!optionSet.has(valueStr);
			const otherText = isOther ? valueStr : "";
			return (
				<div className="space-y-2">
					{field.options?.map((option) => (
						<div key={option} className="flex items-center">
							<input
								type="radio"
								id={`${field.id}-${option}`}
								name={field.id}
								value={option}
								checked={!isOther && value === option}
								onChange={(e) => onChange(e.target.value)}
								className="mr-2"
							/>
							<label htmlFor={`${field.id}-${option}`}>
								{option}
							</label>
						</div>
					))}
					{field.allowOther && (
						<input
							type="text"
							value={otherText}
							onChange={(e) => onChange(e.target.value)}
							placeholder="Sonstiges – eigene Antwort…"
							className={`border rounded-md px-2 py-1 text-sm max-w-sm transition-colors ${
								isOther
									? "border-blue-600 bg-blue-50 text-blue-900"
									: ""
							}`}
						/>
					)}
				</div>
			);
		}

		case "chips": {
			const isMulti = !!field.multi;
			const optionSet = new Set(field.options ?? []);

			const valueArr: string[] = isMulti
				? Array.isArray(value)
					? (value as string[])
					: []
				: [];
			const valueStr = !isMulti && typeof value === "string" ? value : "";

			const otherText = field.allowOther
				? isMulti
					? valueArr.find((v) => !optionSet.has(v)) ?? ""
					: valueStr && !optionSet.has(valueStr)
						? valueStr
						: ""
				: "";
			const isOther = !!otherText;

			const isChipSelected = (option: string) =>
				isMulti ? valueArr.includes(option) : valueStr === option;

			const toggleChip = (option: string) => {
				if (isMulti) {
					const next = valueArr.includes(option)
						? valueArr.filter((v) => v !== option)
						: [...valueArr, option];
					onChange(next);
				} else {
					onChange(option);
				}
			};

			const handleOtherTextChange = (text: string) => {
				if (isMulti) {
					const withoutOther = valueArr.filter((v) =>
						optionSet.has(v)
					);
					onChange(text ? [...withoutOther, text] : withoutOther);
				} else {
					onChange(text);
				}
			};

			return (
				<div>
					<div className="flex flex-wrap gap-2">
						{field.options?.map((option) => {
							const selected = isChipSelected(option);
							return (
								<button
									type="button"
									key={option}
									onClick={() => toggleChip(option)}
									className={`rounded-full border px-3 py-1 text-sm transition-colors ${
										selected
											? "bg-blue-600 border-blue-600 text-white"
											: "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
									}`}
								>
									{option}
								</button>
							);
						})}
						{field.allowOther && (
							<div
								className={`rounded-full border flex items-center pl-3 pr-3 py-0.5 text-sm transition-colors ${
									isOther
										? "bg-blue-600 border-blue-600 text-white"
										: "bg-white border-gray-300 text-gray-700"
								}`}
							>
								<input
									type="text"
									value={otherText}
									onChange={(e) =>
										handleOtherTextChange(e.target.value)
									}
									placeholder="Sonstiges…"
									className={`bg-transparent border-0 outline-none text-sm py-0.5 min-w-[100px] w-32 ${
										isOther
											? "text-white placeholder-white/60"
											: "text-gray-700 placeholder-gray-400"
									}`}
								/>
							</div>
						)}
					</div>
				</div>
			);
		}

		case "checkbox":
			return (
				<div className="space-y-2">
					{field.options?.map((option) => (
						<div key={option} className="flex items-center">
							<input
								type="checkbox"
								id={`${field.id}-${option}`}
								value={option}
								checked={(value || []).includes(option)}
								onChange={(e) => {
									const currentValues = value || [];
									const newValues = e.target.checked
										? [...currentValues, option]
										: currentValues.filter(
												(v: string) => v !== option
											);
									onChange(newValues);
								}}
								className="mr-2"
							/>
							<label htmlFor={`${field.id}-${option}`}>
								{option}
							</label>
						</div>
					))}
				</div>
			);

		case "date":
			return (
				<input
					type="date"
					value={value || ""}
					onChange={(e) => onChange(e.target.value)}
					className="w-full border rounded-md px-3 py-2"
				/>
			);

		case "time":
			return (
				<input
					type="time"
					value={value || ""}
					onChange={(e) => onChange(e.target.value)}
					className="w-full border rounded-md px-3 py-2"
				/>
			);

		case "file":
			return <FileUploadField value={value} onChange={onChange} />;

		case "odontogram":
			return (
				<Odontogram
					selectedTeeth={value || []}
					onTeethChange={onChange}
					maxSelections={
						field.defaultValue
							? (field.defaultValue as string[]).length
							: undefined
					}
					odontogramFields={field.odontogramFields}
				/>
			);

		case "appointment":
			return (
				<AppointmentField
					field={field}
					value={value}
					onChange={onChange}
				/>
			);

		default:
			return null;
	}
};

export default FormPreviewField;
